import { Component, Inject, LOCALE_ID, OnDestroy, OnInit, signal } from '@angular/core';
import * as moment from 'moment';
import { filter, Observable, Subscription, take } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { customerConfig } from '@uxdf/direct-shared-lib';
import { ReefDatePipe } from '@seco/core';
import { AvcInvoicesState, InvoiceHierarchyItem } from '../redux/reducers/reducers-avc-invoices';
import { GetAvcInvoices, GetAvcInvoicesHierarchy } from '../redux/actions/actions-avc-invoices';
import { avcInvoicesSelector, getCustomerConfigState } from './../../../../redux/reducers';
import { isOn } from 'feature-toggle-service';
import { InvoiceDownloadAPI } from '../../../../redux/models/invoices/invoice-download-api';

interface IAlert {
  id: number;
  type: string;
  message?: string;
}

const INVOICE_NUMBER_LENGTH = 10;

@Component({
  standalone: false,
  selector: 'app-avc-invoices',
  templateUrl: './avc-invoices.component.html',
  styleUrls: ['./avc-invoices.component.scss']
})
export class AvcInvoicesComponent implements OnInit, OnDestroy {
  public avcInvoicesState$: Observable<AvcInvoicesState>;
  invoiceData: Map<string, InvoiceDownloadAPI.IInvoiceResponse[]> = new Map<string, InvoiceDownloadAPI.IInvoiceResponse[]>();
  loadingError: string;
  // empty comment
  avcInvoicesHierarchy: InvoiceHierarchyItem[] = [];

  allData: Map<string, BillingDocumentTableRow> = new Map<string, BillingDocumentTableRow>();
  downloadInvoicePdf = $localize`:@@amadeus-direct.avc-invoices.download-invoice-button:Download invoice`;
  downloadBillingReportCsv = $localize`:@@amadeus-direct.avc-invoices.download-billing-report-button:Download billing report`;
  noInvoicePdf = $localize`:@@amadeus-direct.avc-invoices.no-invoice-pdf:No invoice available`;
  noBillingReportCsv = $localize`:@@amadeus-direct.avc-invoices.no-billing-report-csv:No billing report available`;

  viewAVCInvoices: boolean;

  selectedOffice: { value: string; label: string };
  officesList: { value: string; label: string }[] = [];

  public readonly alert = signal<IAlert>({
    id: 1,
    type: 'info',
    message:$localize`:@@amadeus-direct.avc-invoices.disclaimer:Your invoices will move to a new portal soon. Stay tuned for more updates.`
  });

  public closeAlert() {
    this.alert.update(() => null);
  }

  private customerConfigSubscription: Subscription;

  private readonly MONTH_YEAR_FORMAT = 'MMMM y';

  private readonly monthsMap = new Map<string, string>();

  constructor(
    @Inject(LOCALE_ID) private readonly locale: string,
    private readonly store: Store<any>,
    private readonly reefDatePipe: ReefDatePipe
  ) {
    this.monthsMap.set('January', $localize`:@@amadeus-direct.avc-invoices.january:January`);
    this.monthsMap.set('February', $localize`:@@amadeus-direct.avc-invoices.february:February`);
    this.monthsMap.set('March', $localize`:@@amadeus-direct.avc-invoices.march:March`);
    this.monthsMap.set('April', $localize`:@@amadeus-direct.avc-invoices.april:April`);
    this.monthsMap.set('May', $localize`:@@amadeus-direct.avc-invoices.may:May`);
    this.monthsMap.set('June', $localize`:@@amadeus-direct.avc-invoices.june:June`);
    this.monthsMap.set('July', $localize`:@@amadeus-direct.avc-invoices.july:July`);
    this.monthsMap.set('August', $localize`:@@amadeus-direct.avc-invoices.august:August`);
    this.monthsMap.set('September', $localize`:@@amadeus-direct.avc-invoices.september:September`);
    this.monthsMap.set('October', $localize`:@@amadeus-direct.avc-invoices.october:October`);
    this.monthsMap.set('November', $localize`:@@amadeus-direct.avc-invoices.november:November`);
    this.monthsMap.set('December', $localize`:@@amadeus-direct.avc-invoices.december:December`);
  }

  ngOnInit(): void {
    this.store.select(getCustomerConfigState).subscribe(config => {
      this.viewAVCInvoices = config.customerConfig?.is_avc_invoices;
    });
  
    this.customerConfigSubscription = this.store.pipe(select(customerConfig)).subscribe(response => {
      if (response.customerConfig && !response.isLoading) {
        this.selectedOffice = {
          value: response.customerConfig.office_codes[0],
          label: response.customerConfig.office_codes[0]
        };
  
        if (isOn('INVOCING_NETWORK_LEVEL_A')) {
          this.store.dispatch(new GetAvcInvoicesHierarchy());
  
          this.store.pipe(
            select(avcInvoicesSelector),
            filter(state => state.avcInvoicesHierarchy.length > 0),
            take(1)
          ).subscribe((state) => {
            const firstOfficeId = state.avcInvoicesHierarchy[0].officeId;
            this.store.dispatch(new GetAvcInvoices(firstOfficeId));
          });
        } else {
          this.store.dispatch(new GetAvcInvoices(response.customerConfig.office_codes[0]));
        }
  
        this.avcInvoicesState$ = this.store.pipe(select(avcInvoicesSelector));
        this.subscribeToAvcInvoices();
      }
    });
  }

  shouldDisplayOfficeSelector(): boolean {
    return isOn('INVOCING_NETWORK_LEVEL_A');
  }

  getSelectedOffice() {
    return this.selectedOffice.value;
  }

  ngOnDestroy() {
    this.customerConfigSubscription.unsubscribe();
  }

  asIsOrder() {
    return 1;
  }

  onOfficeChange() {
    const selectedOffice = this.getSelectedOffice();
    if (!this.invoiceData.has(selectedOffice)) {
      this.store.dispatch(new GetAvcInvoices(selectedOffice));
    } else {
      this.processInvoices(this.invoiceData.get(selectedOffice));
    }
  }

  private subscribeToAvcInvoices() {
    this.avcInvoicesState$.subscribe(state => {
      this.invoiceData = state.avcInvoices;
      this.loadingError = state.loadingError;
      
      this.avcInvoicesHierarchy = state.avcInvoicesHierarchy;
      this.officesList = this.avcInvoicesHierarchy.map(hierarchyItem => {
        return {
          value: hierarchyItem.officeId,
          label: `${hierarchyItem.companyName} (${hierarchyItem.officeId})`
        }
      });
      if (this.officesList.length > 0 && this.isFirstLoadFromSubscription()) {
        this.selectedOffice = this.officesList[0];
      }

      this.processInvoices(this.invoiceData.get(this.getSelectedOffice()));
      this.sortInvoicesByDocumentName();
    });
  }

  isFirstLoadFromSubscription() {
    return this.selectedOffice.label === this.selectedOffice.value;
  }

  private processInvoices(invoices: InvoiceDownloadAPI.IInvoiceResponse[]) {
    this.populateEmptyMonthYearToBillingDocumentsMap()
    
    for (const invoice of invoices) {
      this.populateInvoices(invoice);
      this.populateBillingReports(invoice);
    }
    this.sortInvoicesByDocumentName();
  }

  private sortInvoicesByDocumentName() {
    this.allData.forEach(billingTableRow => {
      billingTableRow.invoices.sort((a, b) => {
        if (a.name > b.name) {
          return -1;
        }
        if (a.name < b.name) {
          return 1;
        }
        return 0;
      });
    });
  }

  private populateEmptyMonthYearToBillingDocumentsMap() {
    const date = new Date();
    date.setDate(1);
    // invoicing should start from previous month
    date.setMonth(date.getMonth() - 1);

    // when locales are fixed, change 'en_US' to this.locale.toString() on both occurences
    let formattedDate = this.reefDatePipe.transform(date, this.MONTH_YEAR_FORMAT, '', 'en-US');
    this.allData.set(formattedDate, {
      monthYear: formattedDate,
      invoices: [],
      billingReport: null
    });

    for (let month = 0; month < 11; month++) {
      date.setMonth(date.getMonth() - 1);
      formattedDate = this.reefDatePipe.transform(date, this.MONTH_YEAR_FORMAT, '', 'en-US');
      this.allData.set(formattedDate, {
        monthYear: formattedDate,
        invoices: [],
        billingReport: null
      });
    }
  }

  private populateInvoices(invoice: InvoiceDownloadAPI.IInvoiceResponse) {
    const invoiceMonthYear = this.getInvoiceMonthYear(invoice.name);
    if (invoiceMonthYear) {
      const formattedInvoiceMonthYear = this.formatDate(invoiceMonthYear);
      this.allData.has(formattedInvoiceMonthYear) &&
        this.allData
          .get(formattedInvoiceMonthYear)
          .invoices.push({ url: invoice.url, name: this.getInvoiceName(invoice.name) });
    }
  }

  private populateBillingReports(invoice: InvoiceDownloadAPI.IInvoiceResponse) {
    const billingReportMonth = this.getBillingReportMonth(invoice.name);
    if (billingReportMonth) {
      const formattedBillingReportMonthYear = this.formatDate(billingReportMonth);
      if (this.allData.has(formattedBillingReportMonthYear)) {
        this.allData.get(formattedBillingReportMonthYear).billingReport = invoice;
      }
    }
  }

  private getInvoiceName(invoiceName: string): string {
    const nameParts = invoiceName.split('_');
    const extractedInvoiceName = nameParts[0].substring(nameParts[0].length - INVOICE_NUMBER_LENGTH);

    return `${$localize`:@@amadeus-direct.avc-invoices.download:Download`} ${extractedInvoiceName}`;
  }

  private getInvoiceMonthYear(invoiceName: string): string {
    const nameParts = invoiceName.split('_');

    if (nameParts[2].length < 8) {
      return null;
    }

    return nameParts[2].substring(0, 8);
  }

  getMonthFromMonthYear(monthYear: string): string {
    return this.monthsMap.get(monthYear.split(' ')[0]);
  }

  getYearFromMonthYear(monthYear: string): string {
    return monthYear.split(' ')[1];
  }

  private getBillingReportMonth(billingReportName: string): string {
    const nameParts = billingReportName.split('_');

    if (nameParts[2].length >= 8) {
      return null;
    }

    return nameParts[1].substring(0, 8);
  }

  private formatDate(date: string): string {
    const d = moment(date);
    return d.isValid() ? d.format(this.MONTH_YEAR_FORMAT) : '';
  }
}

interface BillingDocumentTableRow {
  monthYear: string;
  invoices: Invoice[];
  billingReport: BillingReport;
}

interface Invoice {
  url: string;
  name: string;
}

interface BillingReport {
  url?: string;
  name?: string;
}
